import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Icon, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, useToast, IconButton, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  useDisclosure, Code, Collapse,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiFileText, FiChevronRight, FiChevronDown, FiClock, FiPauseCircle,
} from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const lastRunColor = (status) => {
  switch (status) {
    case 'succeeded': return 'green';
    case 'failed': return 'red';
    case 'running': return 'blue';
    default: return 'gray';
  }
};

const jobStatusColor = (status) => {
  switch (status) {
    case 'Succeeded': return 'green';
    case 'Failed': return 'red';
    case 'Running': return 'blue';
    default: return 'gray';
  }
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

function CronJobRuns({ cronjobName, onViewLogs }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/k8s/cronjobs/${cronjobName}/jobs/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => { setJobs(data.jobs || []); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [cronjobName]);

  if (loading) return <Box textAlign="center" py={6}><Spinner size="md" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{error}</Alert>;
  if (jobs.length === 0) return <Text fontSize="sm" color="gray.500" py={2}>No runs yet.</Text>;

  return (
    <Table variant="simple" size="sm" bg="white" borderRadius="md">
      <Thead bg="gray.100">
        <Tr>
          <Th>Job</Th>
          <Th>Status</Th>
          <Th>Started</Th>
          <Th>Completed</Th>
          <Th>Duration</Th>
          <Th>Logs</Th>
        </Tr>
      </Thead>
      <Tbody>
        {jobs.map((job) => (
          <Tr key={job.job_name}>
            <Td><Text fontSize="xs" fontFamily="mono">{job.job_name}</Text></Td>
            <Td><Badge colorScheme={jobStatusColor(job.status)}>{job.status}</Badge></Td>
            <Td><Text fontSize="xs" color="gray.500">{fmt(job.start_time)}</Text></Td>
            <Td><Text fontSize="xs" color="gray.500">{fmt(job.completion_time)}</Text></Td>
            <Td><Text fontSize="xs" color="gray.500">{formatDuration(job.duration_seconds)}</Text></Td>
            <Td>
              <Tooltip label="View logs">
                <IconButton
                  icon={<FiFileText />}
                  size="xs"
                  variant="ghost"
                  colorScheme="blue"
                  onClick={() => onViewLogs(job.job_name)}
                  aria-label="View run logs"
                />
              </Tooltip>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function CronJobs() {
  const [cronjobs, setCronjobs] = useState([]);
  const [namespace, setNamespace] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [selectedLogs, setSelectedLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const fetchCronjobs = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/k8s/cronjobs/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setCronjobs(data.cronjobs || []);
        setNamespace(data.namespace || '');
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { fetchCronjobs(); }, [fetchCronjobs]);

  const toggleExpand = (name) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const viewLogs = async (jobName) => {
    setLogsLoading(true);
    setSelectedLogs({ job: jobName, logs: '' });
    onOpen();
    try {
      const res = await fetch(`${API_BASE}/k8s/jobs/${jobName}/logs/?tail=200`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSelectedLogs({ job: jobName, logs: data.logs || 'No logs available' });
    } catch (err) {
      setSelectedLogs({ job: jobName, logs: `Error: ${err.message}` });
      toast({ title: 'Failed to load logs', description: err.message, status: 'error', duration: 5000 });
    }
    setLogsLoading(false);
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">CronJobs</Heading>
          <Text color="gray.500">
            {cronjobs.length} cron jobs in namespace <Badge colorScheme="blue">{namespace}</Badge>
            {' · '}Click a row to see run history
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchCronjobs}>Refresh</Button>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th width="30px" px={2}></Th>
              <Th>Name</Th>
              <Th>Schedule</Th>
              <Th>Last Run</Th>
              <Th>Next Run</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cronjobs.map((cj) => {
              const isExpanded = expanded.has(cj.name);
              const lastRunTime = cj.last_schedule_time;
              return (
                <React.Fragment key={cj.name}>
                  <Tr
                    _hover={{ bg: 'gray.50' }}
                    cursor="pointer"
                    onClick={() => toggleExpand(cj.name)}
                    bg={isExpanded ? 'blue.50' : undefined}
                  >
                    <Td px={2} width="30px">
                      <Icon
                        as={isExpanded ? FiChevronDown : FiChevronRight}
                        color={isExpanded ? 'blue.500' : 'gray.400'}
                        boxSize={4}
                      />
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Icon as={FiClock} color="gray.400" boxSize={4} />
                        <Text fontWeight="medium" fontSize="sm">{cj.name}</Text>
                        {cj.active_count > 0 && (
                          <Badge colorScheme="blue" fontSize="xs">running</Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td><Code fontSize="xs">{cj.schedule}</Code></Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Badge colorScheme={lastRunColor(cj.last_run_status)}>{cj.last_run_status}</Badge>
                        <Text fontSize="xs" color="gray.500">{fmt(lastRunTime)}</Text>
                      </VStack>
                    </Td>
                    <Td>
                      {cj.suspend ? (
                        <HStack spacing={1} color="orange.500">
                          <Icon as={FiPauseCircle} boxSize={4} />
                          <Text fontSize="xs">Suspended</Text>
                        </HStack>
                      ) : (
                        <Text fontSize="xs" color="gray.500">{fmt(cj.next_schedule_time)}</Text>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme={cj.suspend ? 'orange' : 'green'}>
                        {cj.suspend ? 'Suspended' : 'Active'}
                      </Badge>
                    </Td>
                  </Tr>

                  {isExpanded && (
                    <Tr>
                      <Td colSpan={6} p={0} borderBottom="2px solid" borderColor="blue.100">
                        <Collapse in={isExpanded} animateOpacity>
                          <Box bg="gray.50" px={6} py={4}>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" mb={3}>
                              Run History
                            </Text>
                            <CronJobRuns cronjobName={cj.name} onViewLogs={viewLogs} />
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Logs Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Logs: {selectedLogs?.job}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {logsLoading ? (
              <Box textAlign="center" py={10}><Spinner /></Box>
            ) : (
              <Code
                display="block"
                whiteSpace="pre-wrap"
                p={4}
                bg="gray.900"
                color="green.300"
                borderRadius="md"
                fontSize="xs"
                maxH="60vh"
                overflowY="auto"
              >
                {selectedLogs?.logs}
              </Code>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default CronJobs;
